import MVTTruck from "assets/images/MVT_Truck.png";
// PDF -> PNG page images, generated at build time by scripts/convert-pdfs.mjs.
// Maps each `pdfDocument` path to an array of rendered page image paths so the
// site can display an uploaded PDF as plain <img> pages (tablet-safe) instead
// of an embedded PDF.
import pdfPages from "./pdf-pages.json";

// Normalize any Vimeo URL shape into a canonical player-embed URL.
// Editors routinely paste the wrong link from Vimeo (the "Manage" page URL,
// a channel URL, the public share URL, etc.). All of those must resolve to
// https://player.vimeo.com/video/<id>[?h=<hash>] or the player shows
// "This video does not exist." This extracts the numeric id (and optional
// unlisted-video privacy hash) from whatever was pasted and rebuilds it.
export const normalizeVimeoUrl = (raw) => {
  if (!raw) return "";
  const url = String(raw).trim();
  // Already a canonical player-embed URL — leave it alone.
  if (/player\.vimeo\.com\/video\/\d+/.test(url)) return url;
  // Extract the numeric video id from any shape:
  //  - full URL: vimeo.com/manage/videos/<id>, vimeo.com/<id>, channel URLs…
  //  - a bare id the editor typed after the "https://vimeo.com/" prefix
  const idMatch =
    url.match(/vimeo\.com\/(?:[\w-]+\/)*?(\d+)/i) || url.match(/(\d{6,})/);
  if (!idMatch) return url; // no id found — pass through unchanged
  const id = idMatch[1];
  // Unlisted videos carry a privacy hash, either as ?h=… or as the path
  // segment right after the id (vimeo.com/<id>/<hash>).
  const hashMatch =
    url.match(/[?&]h=([A-Za-z0-9]+)/) ||
    url.match(new RegExp(`${id}/([A-Za-z0-9]+)`));
  const hash = hashMatch ? hashMatch[1] : "";
  return `https://player.vimeo.com/video/${id}${hash ? `?h=${hash}` : ""}`;
};

// Load all videos from CMS (content/videos/*.json)
const loadCMSVideos = () => {
  try {
    const context = require.context('../../content/videos', false, /\.(json|md)$/);
    const cmsVideos = context.keys()
      .filter(key => key !== './.gitkeep')
      .map((key, index) => {
        try {
          const content = context(key);
          let videoData;

          if (key.endsWith('.json')) {
            videoData = content.default || content;
          } else if (key.endsWith('.md')) {
            videoData = content.default || content;
          }

          // Accept videos with: vimeoUrl, pdfDocument, transcript (single), or transcriptImages (multiple)
          const hasContent = videoData.vimeoUrl || videoData.pdfDocument || videoData.transcript ||
            (videoData.transcriptImages && videoData.transcriptImages.length > 0);
          if (!videoData || !videoData.title || !hasContent) {
            console.warn(`Skipping invalid video file: ${key}`, videoData);
            return null;
          }

          // Handle transcript: support both single image and multiple images array
          let transcriptArray = null;
          if (videoData.transcriptImages && videoData.transcriptImages.length > 0) {
            transcriptArray = videoData.transcriptImages.map(img => img.image || img);
          } else if (videoData.transcript) {
            transcriptArray = [videoData.transcript];
          }

          const slug = key.replace(/^\.\//, '').replace(/\.(json|md)$/, '');
          // PNG pages rendered from an uploaded PDF (build-time conversion).
          const pdfImages =
            (videoData.pdfDocument && pdfPages[videoData.pdfDocument]) || null;

          // Sort key: prefer the explicit `date`, else the YYYY-MM-DD prefix
          // baked into the filename/slug. Used to show newest videos first.
          const dateSource =
            videoData.date || (slug.match(/^\d{4}-\d{2}-\d{2}/) || [])[0] || '';
          const sortTime = dateSource ? new Date(dateSource).getTime() : 0;

          return {
            // Explicit `id` is authoritative. Fallback is positional, so it
            // drifts whenever files are added/removed. Base is 1000 to stay
            // clear of the pinned 0-146 range — pin an explicit id on any
            // video you intend to link to directly.
            id: videoData.id !== undefined ? videoData.id : (1000 + index),
            // Stable identifier derived from the filename (e.g.
            // "2026-06-16-traxen-first-time-setup-process"). Never drifts when
            // files are added/removed, so it's the preferred link target.
            slug,
            title: videoData.title,
            videoUrl: normalizeVimeoUrl(videoData.vimeoUrl),
            thumbnailUrl: videoData.thumbnail || MVTTruck,
            videoSubtitleUrl: videoData.subtitleUrl || "",
            transcript: transcriptArray,
            pdfDocument: videoData.pdfDocument || null,
            // Rendered PNG pages for an uploaded PDF (null if none).
            pdfImages,
            sortTime,
          };
        } catch (fileError) {
          console.error(`Error loading video file ${key}:`, fileError);
          return null;
        }
      })
      .filter(video => video !== null);
    // Newest videos on top. Ties (or missing dates) keep their original order.
    cmsVideos.sort((a, b) => b.sortTime - a.sortTime);
    return cmsVideos;
  } catch (error) {
    console.log('No CMS videos found yet. Videos will appear after adding them via admin panel.', error);
    return [];
  }
};

const videos = loadCMSVideos();

export default videos;
